import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Trash2, FileText, AlertCircle, X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CertificationsSection = ({ profile }) => {
  const { t } = useTranslation();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    issue_date: '',
    expiry_date: '',
    file: null
  });

  // Only show for job_seeker and contractor
  const canManageCerts = profile?.role === 'job_seeker' || profile?.role === 'contractor';

  useEffect(() => {
    if (canManageCerts) {
      fetchCertifications();
    } else {
      setLoading(false);
    }
  }, [canManageCerts]);

  const fetchCertifications = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/certifications', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      setCertifications(response.data.certifications || []);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
      toast.error(t('certifications.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('certifications.selectValidFile', 'Please select a PDF, image, or Word document'));
      return;
    }

    // Validate file size: 20MB for images (compression will bring it down), 10MB for PDFs/docs
    const isPdf = file.type === 'application/pdf';
    const isDoc = file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const maxSize = (isPdf || isDoc) ? 10 * 1024 * 1024 : 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error((isPdf || isDoc) ? 'Document must be less than 10MB' : t('certifications.imageTooLarge', 'Image must be less than 20MB'));
      return;
    }

    setFormData({ ...formData, file });
  };

  const handleAddCertification = async () => {
    if (!formData.title || !formData.issue_date || !formData.file) {
      toast.error(t('certifications.fillTitleIssueDate'));
      return;
    }

    setUploading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Not authenticated');
      }

      let filePath;
      const fileType = formData.file.type;

      // Check if it's a PDF or an image
      if (fileType === 'application/pdf') {
        // PDFs go directly to Supabase (private bucket - no public URL)
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const fileName = `${user.id}/${Date.now()}_${formData.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;

        const { error: uploadError } = await supabase.storage
          .from('certifications')
          .upload(fileName, formData.file, {
            contentType: 'application/pdf'
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(t('certifications.failedToUpload'));
        }

        // Store the path, not public URL (bucket should be private)
        filePath = fileName;
      } else {
        // Images go through backend API (supports HEIC/HEIF)
        // Compress image before uploading
        const compressed = await compressImage(formData.file, 'document');

        const imageFormData = new FormData();
        imageFormData.append('file', compressed);

        const uploadResponse = await api.post('/api/upload/certificate', imageFormData, {
          headers: {
            'Authorization': `Bearer ${session.session.access_token}`
          },
          timeout: UPLOAD_TIMEOUT,
        });

        // Backend now returns file_path instead of file_url
        filePath = uploadResponse.data.file_path;
      }

      // Create certification via API (store path for secure access)
      await api.post(
        '/api/certifications',
        {
          title: formData.title,
          file_url: filePath,  // This is now a path, not a URL
          issue_date: formData.issue_date,
          expiry_date: formData.expiry_date
        },
        {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`
          }
        }
      );

      toast.success(t('certifications.certificationAdded'));
      setShowAddDialog(false);
      setFormData({ title: '', issue_date: '', expiry_date: '', file: null });
      await fetchCertifications();
    } catch (error) {
      console.error('Add certification error:', error);
      toast.error(error.response?.data?.detail || error.message || t('certifications.failedToAdd'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (certId) => {
    if (!window.confirm(t('certifications.areYouSure'))) {
      return;
    }

    setDeleting(certId);
    try {
      const { data: session } = await supabase.auth.getSession();
      await api.delete(`/api/certifications/${certId}`, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        }
      });

      toast.success(t('certifications.certificationDeleted'));
      await fetchCertifications();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('certifications.failedToDelete'));
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false; // No expiry date means it never expires
    return new Date(expiryDate) < new Date();
  };

  const handleDownloadCertificate = async (cert) => {
    if (!cert?.id) return;

    setDownloading(cert.id);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in to download certifications');
        return;
      }

      // Use the secure download endpoint
      const response = await api.get(`/api/certifications/${cert.id}/download`, {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;

        // Get filename from Content-Disposition header or use cert title
        const contentDisposition = response.headers['content-disposition'];
        let filename = cert.title || 'certification';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '');
          }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(t('certifications.downloaded', 'Certificate downloaded'));
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download certificate:', error);
      toast.error(t('certifications.downloadFailed', 'Failed to download certificate'));
    } finally {
      setDownloading(null);
    }
  };

  if (!canManageCerts) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="spinner" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="certifications-section">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading text-xl font-semibold">{t('certifications.certificationsLicenses')}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('certifications.addUpTo5')} ({certifications.length}/5)
              </p>
            </div>
            {certifications.length < 5 && (
              <Button
                onClick={() => setShowAddDialog(true)}
                size="sm"
                data-testid="add-certification-btn"
              >
                <Upload size={16} className="mr-2" />
                {t('certifications.add')}
              </Button>
            )}
          </div>

          {certifications.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg" data-testid="no-certifications">
              <FileText size={48} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">{t('certifications.noCertifications')}</p>
              <Button onClick={() => setShowAddDialog(true)} variant="outline">
                <Upload size={16} className="mr-2" />
                {t('certifications.addFirstCertification')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {certifications.map((cert) => (
                <Card
                  key={cert.id}
                  className="border"
                  data-testid={`cert-${cert.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={16} className="text-primary flex-shrink-0" />
                          <h3 className="font-medium truncate">{cert.title}</h3>
                          {cert.expiry_date ? (
                            isExpired(cert.expiry_date) ? (
                              <Badge variant="destructive" className="text-xs">{t('certifications.expired')}</Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-success/10 text-success border-success/20 text-xs">
                                {t('certifications.valid')}
                              </Badge>
                            )
                          ) : (
                            <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-xs">
                              {t('certifications.noExpiry')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div>{t('certifications.issued')}: {formatDate(cert.issue_date)}</div>
                          {cert.expiry_date && <div>{t('certifications.expires')}: {formatDate(cert.expiry_date)}</div>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadCertificate(cert)}
                          disabled={downloading === cert.id}
                          data-testid={`download-cert-${cert.id}`}
                        >
                          {downloading === cert.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Download size={16} />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(cert.id)}
                          disabled={deleting === cert.id}
                          data-testid={`delete-cert-${cert.id}`}
                        >
                          {deleting === cert.id ? (
                            <div className="spinner w-4 h-4" />
                          ) : (
                            <Trash2 size={16} className="text-destructive" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Certification Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-cert-dialog">
          <DialogHeader>
            <DialogTitle>{t('certifications.addCertification')}</DialogTitle>
            <DialogDescription>
              {t('certifications.uploadToStandOut')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cert-title">
                {t('certifications.certificationTitle')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cert-title"
                placeholder={t('certifications.certificationTitlePlaceholder')}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={200}
                data-testid="cert-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="issue-date">
                  {t('certifications.issueDate')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="issue-date"
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  data-testid="cert-issue-date-input"
                />
              </div>

              <div>
                <Label htmlFor="expiry-date">
                  {t('certifications.expiryDate')} <span className="text-muted-foreground text-xs">({t('certifications.optional')})</span>
                </Label>
                <Input
                  id="expiry-date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  data-testid="cert-expiry-date-input"
                  placeholder={t('certifications.leaveEmptyNoExpiry')}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('certifications.leaveEmptyNoExpiry')}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="cert-file">
                {t('certifications.certificateFile')} <span className="text-destructive">*</span>
              </Label>
              <div className="mt-2">
                <label
                  htmlFor="cert-file"
                  className="flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors"
                >
                  {formData.file ? (
                    <div className="flex items-center gap-2">
                      <FileText size={20} className="text-primary" />
                      <span className="text-sm">{formData.file.name}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setFormData({ ...formData, file: null });
                        }}
                        className="ml-2"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload size={24} className="mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">{t('certifications.clickToUploadPdf')}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t('verification.max5mb')}</p>
                    </div>
                  )}
                </label>
                <input
                  id="cert-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="cert-file-input"
                />
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-info/10 border border-info/20 rounded-lg">
              <AlertCircle size={16} className="text-info mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">
                {t('certifications.certDetailsVisible')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setFormData({ title: '', issue_date: '', expiry_date: '', file: null });
              }}
              disabled={uploading}
            >
              {t('verification.cancel')}
            </Button>
            <Button
              onClick={handleAddCertification}
              disabled={uploading}
              data-testid="submit-cert-btn"
            >
              {uploading ? (
                <>
                  <div className="spinner w-4 h-4 mr-2" />
                  {t('certifications.uploadingCert')}
                </>
              ) : (
                t('certifications.addingCertification')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CertificationsSection;
